import { useState, useEffect } from "react";
import { listSkills, activateSkill, deactivateSkill } from "../lib/api";

interface Skill {
  id: string;
  name: string;
  description: string;
  icon: string;
  system_prompt: string;
  tool_allowlist: string[] | null;
  example_prompts: string[];
  scope: string;
  enabled: boolean;
  activated: boolean;
}

interface SkillsViewProps {
  onAskAI?: (context: string) => void;
}

export function SkillsView({ onAskAI }: SkillsViewProps) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listSkills("ai");
      setSkills(data.skills || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  const toggle = async (skill: Skill) => {
    setToggling(skill.id);
    try {
      if (skill.activated) {
        await deactivateSkill(skill.id);
      } else {
        await activateSkill(skill.id);
      }
      setSkills((prev) =>
        prev.map((s) => s.id === skill.id ? { ...s, activated: !s.activated } : s)
      );
    } catch {} finally {
      setToggling(null);
    }
  };

  const activeSkills = skills.filter((s) => s.activated);
  const inactiveSkills = skills.filter((s) => !s.activated);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-foreground">Skills</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Activate skills to give the AI specialised capabilities and behaviour during your chats.
            Active skills are injected automatically into every conversation.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Active skills summary */}
            {activeSkills.length > 0 && (
              <div className="rounded-xl bg-primary/5 border border-primary/20 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-primary mb-1">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  {activeSkills.length} active {activeSkills.length === 1 ? "skill" : "skills"}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {activeSkills.map((s) => (
                    <span key={s.id} className="flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5 font-medium">
                      {s.icon} {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Skill grid */}
            <div className="grid gap-3 sm:grid-cols-2">
              {skills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  expanded={expanded === skill.id}
                  toggling={toggling === skill.id}
                  onToggle={() => toggle(skill)}
                  onExpand={() => setExpanded(expanded === skill.id ? null : skill.id)}
                  onTryInChat={() => {
                    const example = skill.example_prompts?.[0];
                    if (example) onAskAI?.(example);
                  }}
                />
              ))}
            </div>

            {skills.length === 0 && (
              <div className="text-center py-20 text-muted-foreground text-sm">
                No skills available.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SkillCard({
  skill,
  expanded,
  toggling,
  onToggle,
  onExpand,
  onTryInChat,
}: {
  skill: Skill;
  expanded: boolean;
  toggling: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onTryInChat: () => void;
}) {
  return (
    <div className={`rounded-xl border transition-all ${
      skill.activated
        ? "bg-primary/5 border-primary/30"
        : "bg-card border-border"
    }`}>
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-background border border-border flex items-center justify-center text-xl flex-shrink-0">
              {skill.icon}
            </div>
            <div>
              <h3 className="font-medium text-foreground text-sm">{skill.name}</h3>
              {skill.activated && (
                <span className="text-[10px] text-primary font-medium">Active</span>
              )}
            </div>
          </div>

          {/* Toggle */}
          <button
            onClick={onToggle}
            disabled={toggling}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
              skill.activated ? "bg-primary" : "bg-muted-foreground/30"
            }`}
            title={skill.activated ? "Deactivate" : "Activate"}
          >
            <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform duration-200 ${
              skill.activated ? "translate-x-4" : "translate-x-0.5"
            }`} />
          </button>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          {skill.description}
        </p>

        {/* Example prompts */}
        {skill.example_prompts?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {skill.example_prompts.slice(0, 2).map((ex, i) => (
              <button
                key={i}
                onClick={() => onTryInChat()}
                className="text-[10px] px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors truncate max-w-[160px]"
                title={ex}
              >
                "{ex}"
              </button>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex gap-2">
          <button
            onClick={onExpand}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-background transition-colors"
          >
            {expanded ? "Hide" : "Details"}
          </button>
          <button
            onClick={onTryInChat}
            className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
          >
            Try in chat
          </button>
        </div>
      </div>

      {/* Expanded: system prompt preview */}
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          <div>
            <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              System prompt injected when active
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed bg-background rounded-lg p-3 border border-border">
              {skill.system_prompt}
            </p>
          </div>
          {skill.example_prompts?.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                Example prompts
              </div>
              <div className="space-y-1">
                {skill.example_prompts.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => onTryInChat()}
                    className="w-full text-left text-xs text-muted-foreground hover:text-foreground bg-background border border-border rounded-lg px-3 py-1.5 transition-colors"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
